// The Story room's charts are SVG drawn here (no chart library: the CSP allows only the app's own scripts, and the
// charts are few): scales, the calendar axis's steps (CalendarLanes.dateStep), and a hook for a chart's width.
import { useLayoutEffect, useState, type RefObject } from 'react';
import { adding, components, deviceZone, instant, type CalendarUnit } from '@writing-suite/core/story';
import { navigatorLocale } from './common.tsx';

/** A linear scale from a domain onto a range (a zero-width domain maps to the range's middle). */
export function scale(d0: number, d1: number, r0: number, r1: number) {
  const span = d1 - d0;
  return (v: number) => (span === 0 ? (r0 + r1) / 2 : r0 + ((v - d0) / span) * (r1 - r0));
}

/** The inverse of `scale`. */
export function invert(d0: number, d1: number, r0: number, r1: number) {
  const span = r1 - r0;
  return (px: number) => (span === 0 ? d0 : d0 + ((px - r0) / span) * (d1 - d0));
}

/** The element's width, followed as it changes (a first guess until it's measured). */
export function useWidth(ref: RefObject<HTMLElement | null>, fallback = 800): number {
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth || fallback);
    if (typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver(() => setWidth(el.clientWidth || fallback));
    o.observe(el);
    return () => o.disconnect();
  }, [ref, fallback]);
  return width;
}

const DAY = 86_400_000;
type Style = 'weekday' | 'day' | 'month' | 'year';
const STEPS: [CalendarUnit, number, number, Style][] = [
  ['day', 1, DAY, 'weekday'],
  ['day', 2, 2 * DAY, 'day'],
  ['week', 1, 7 * DAY, 'day'],
  ['week', 2, 14 * DAY, 'day'],
  ['month', 1, 30.4 * DAY, 'month'],
  ['month', 2, 60.9 * DAY, 'month'],
  ['month', 3, 91.3 * DAY, 'month'],
  ['month', 6, 182.6 * DAY, 'month'],
  ['year', 1, 365.25 * DAY, 'year'],
  ['year', 2, 730.5 * DAY, 'year'],
  ['year', 5, 1826 * DAY, 'year'],
  ['year', 10, 3652 * DAY, 'year'],
  ['year', 25, 9131 * DAY, 'year'],
  ['year', 100, 36_525 * DAY, 'year'],
];

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(style: Style): Intl.DateTimeFormat {
  const zone = deviceZone();
  const id = 'id' in zone ? zone.id : 'UTC';
  const key = `${style}|${id}`;
  let f = formatters.get(key);
  if (!f) {
    const o: Intl.DateTimeFormatOptions =
      style === 'weekday' ? { weekday: 'short', day: 'numeric', month: 'short' }
        : style === 'day' ? { day: 'numeric', month: 'short' }
          : style === 'month' ? { month: 'short', year: 'numeric' } : { year: 'numeric' };
    try {
      f = new Intl.DateTimeFormat(navigatorLocale(), { ...o, timeZone: id });
    } catch {
      f = new Intl.DateTimeFormat('en-US', o);
    }
    formatters.set(key, f);
  }
  return f;
}

/** `CalendarLanes.dateStep`: the finest calendar step whose labels fit the width (about 90 px each) — days, weeks,
 *  months, quarters, half-years, then years — its ticks from the first time to the last, and the label that suits it. */
export function dateTicks(min: number, max: number, width: number): { ticks: number[]; label: (ms: number) => string } {
  const span = max - min;
  const fits = Math.max(2, Math.floor(width / 90));
  const step = STEPS.find((s) => span / s[2] + 1 <= fits) ?? STEPS[STEPS.length - 1];
  const [unit, count, , style] = step;
  const zone = deviceZone();
  const c = components(min, zone);
  let start: number;
  if (unit === 'day' || unit === 'week') start = instant({ ...c, hour: 0, minute: 0, second: 0, ms: 0 }, zone);
  else if (unit === 'month') start = instant({ ...c, month: c.month - ((c.month - 1) % count), day: 1, hour: 0, minute: 0, second: 0, ms: 0 }, zone);
  else start = instant({ ...c, year: c.year - (((c.year % count) + count) % count), month: 1, day: 1, hour: 0, minute: 0, second: 0, ms: 0 }, zone);
  const ticks: number[] = [];
  for (let t = start, i = 0; t <= max && i < 400; t = adding(unit, count, t, zone), i++) if (t >= min - 1) ticks.push(t);
  if (!ticks.length) ticks.push(min);
  const f = formatter(style);
  return { ticks, label: (ms) => f.format(new Date(ms)) };
}

/** `TimeVsOrderChart.label`: dates for long spans, times of day for short ones. */
export function spanLabel(ms: number, span: number): string {
  const zone = deviceZone();
  const timeZone = 'id' in zone ? zone.id : 'UTC';
  const o: Intl.DateTimeFormatOptions = span > 3 * DAY ? { day: 'numeric', month: 'short', year: 'numeric' }
    : span > DAY ? { day: 'numeric', month: 'short', hour: 'numeric' } : { hour: 'numeric', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat(navigatorLocale(), { ...o, timeZone }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(0, 10);
  }
}

/** `TimeVsOrderChart.ticks`: four evenly spaced times from first to last (one when they're all the same). */
export function evenTicks(times: number[]): number[] {
  if (!times.length) return [];
  const first = Math.min(...times), last = Math.max(...times);
  if (last === first) return [first];
  return [0, 1, 2, 3].map((i) => first + ((last - first) * i) / 3);
}

/** Moves a selection among items in order with the arrow keys (the charts are one tab stop each). */
export function arrowStep<T>(e: React.KeyboardEvent, items: T[], current: T | null | undefined): T | null {
  const i = current === null || current === undefined ? -1 : items.indexOf(current);
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') return items[Math.min(items.length - 1, i + 1)] ?? null;
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') return items[Math.max(0, i - 1)] ?? null;
  if (e.key === 'Home') return items[0] ?? null;
  if (e.key === 'End') return items[items.length - 1] ?? null;
  return null;
}
